import { QueryErrorResetBoundary, useQueryClient } from "@tanstack/react-query";
import {
    Component,
    MutableRefObject,
    ReactNode,
    useCallback,
    useEffect,
    useRef,
    useState
} from "react";
import { useLocation } from "react-router-dom";
import { CenteredErrorIcon } from "./CenteredErrorIcon";
import { errorManager } from "./ErrorManager";
import { usePreload } from "@/frontend/@navigation/PreloadProvider";

export const ErrorBoundary = ({
    children,
    fallbackWrapper
}: {
    children: ReactNode;
    fallbackWrapper?: (children: ReactNode) => ReactNode;
}) => {
    const queryClient = useQueryClient();
    const retryRef = useRef<() => void>(() => {
        void 0;
    });
    const [error, setError] = useState<Error | undefined>(undefined);

    const onError = (error: Error) => {
        setError(error);
    };

    const retry = () => {
        setError(undefined);

        // reset all queries that are in an error state
        // there is an issue with react-query QueryErrorResetBoundary that doesn't reset all child queries with errors, so as a workaround we reset *all* errored queries.
        // the issue affects queries which aren't immediately re-triggered as part of a reset.
        // for example, if a sub component is drawn conditionally because of some state (something like a tab or accordion etc), then this child component will never
        // get it's errored queries reset. (as opposed to queries which are used by the page itself without any user interaction)
        // possiblly to do with this issue https://github.com/TanStack/query/issues/2712
        queryClient
            .getQueryCache()
            .getAll()
            .forEach(query => {
                if (query.state.status === "error") {
                    void queryClient.resetQueries({
                        queryKey: query.queryKey,
                        exact: true
                    });
                }
            });

        retryRef.current();
    };

    return (
        <QueryErrorResetBoundary>
            {({ reset }) => (
                <ClassErrorBoundary
                    fallback={
                        <Fallback
                            error={error}
                            resetQueries={reset}
                            retry={retry}
                            fallbackWrapper={fallbackWrapper}
                        />
                    }
                    onError={onError}
                    retryRef={retryRef}
                >
                    {children}
                </ClassErrorBoundary>
            )}
        </QueryErrorResetBoundary>
    );
};

const Fallback = ({
    error,
    resetQueries,
    retry,
    fallbackWrapper
}: {
    error: Error | undefined;
    resetQueries: () => void;
    retry: () => void;
    fallbackWrapper?: (children: ReactNode) => ReactNode;
}) => {
    const { isPreloading } = usePreload();

    const onRetry = useCallback(() => {
        resetQueries();
        retry();
    }, [resetQueries, retry]);

    // report to global error manager
    useEffect(() => {
        if (isPreloading) {
            return;
        }
        if (error) {
            errorManager.addError(error);
            return errorManager.addRetryCallback(onRetry);
        }
    }, [error, onRetry, isPreloading]);

    // auto retry error if page url changes
    // this is here as well as global error display, because global error may be intentionally hidden by the user, and then further navigation may happen
    const { pathname } = useLocation();
    const pathRef = useRef(pathname);
    useEffect(() => {
        if (isPreloading) {
            return;
        }
        if (pathname !== pathRef.current) {
            onRetry();
        }
    }, [isPreloading, pathname, onRetry]);

    const content = <CenteredErrorIcon />;

    return fallbackWrapper ? fallbackWrapper(content) : content;
};

interface IClassErrorBoundaryProps {
    fallback: ReactNode;
    children: ReactNode;
    onError: (error: Error) => void;
    retryRef: MutableRefObject<() => void>;
}

class ClassErrorBoundary extends Component<
    IClassErrorBoundaryProps,
    { hasError: boolean }
> {
    constructor(props: IClassErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
        this.props.retryRef.current = () => {
            this.setState({ hasError: false });
        };
    }

    static getDerivedStateFromError(_: Error) {
        // Update state so the next render will show the fallback UI.
        return { hasError: true };
    }

    override componentDidCatch(error: Error) {
        this.props.onError(error);
    }

    override render() {
        if (this.state.hasError) {
            return this.props.fallback;
        }

        return this.props.children;
    }
}
